/**
 * User ID helpers.
 *
 * See userIdAtom docs for details on numeric DB ID vs Cognito UUID types.
 * Use helper functions below to safely work with userId values.
 */

/**
 * Check if userId is a temporary Cognito sub (pending OAuth signup).
 */
export function isPendingOAuthUserId(userId: string | undefined | null): boolean {
  if (!userId) {
    return false;
  }

  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  return uuidPattern.test(userId);
}

/**
 * Returns true when API calls that require a database user should be made.
 */
export function shouldFetchUserScoped(userId: string | undefined | null): boolean {
  return !!userId && !isPendingOAuthUserId(userId);
}

/**
 * Safely convert userId to number.
 * Returns undefined if userId is a Cognito UUID or not numeric.
 */
export function toNumericUserId(userId: string | undefined | null): number | undefined {
  if (!userId) {
    return undefined;
  }

  return /^\d+$/.test(userId) ? Number(userId) : undefined;
}

/**
 * Error parsing utilities for auth error handling.
 */

export interface ParsedAuthError {
  name?: string;
  code?: string;
  message: string;
}

/**
 * Parse error from various formats (Error instance, nested error object, plain object).
 *
 * Handles Amplify Hub event data which can be in multiple formats:
 * - Error instance
 * - { error: Error | { name, code, message } }
 * - { name, code, message }
 * - Plain string or unknown
 */
export function parseAuthError(error: unknown): ParsedAuthError {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
    };
  }

  if (error && typeof error === 'object') {
    const obj = error as { error?: unknown; name?: string; code?: string; message?: string };

    if ('error' in obj && obj.error) {
      const nested = obj.error;

      if (nested instanceof Error) {
        return {
          name: nested.name,
          message: nested.message,
        };
      }

      if (nested && typeof nested === 'object') {
        const nestedObj = nested as { name?: string; code?: string; message?: string };

        return {
          name: nestedObj.name,
          code: nestedObj.code,
          message: nestedObj.message || String(nested),
        };
      }

      return {
        message: String(nested),
      };
    }

    return {
      name: obj.name,
      code: obj.code,
      message: obj.message || String(error),
    };
  }

  return {
    message: String(error || 'Unknown error'),
  };
}

/**
 * Check if error indicates a revoked token.
 *
 * Detects NotAuthorizedException with revoked token messages.
 * Matches patterns used by AWS Cognito for token revocation errors.
 */
export function isRevokedTokenError(error: ParsedAuthError): boolean {
  const isNotAuthorizedException = error.name === 'NotAuthorizedException';

  const mentionsRevoked = error.message.toLowerCase().includes('revoked');

  return isNotAuthorizedException && mentionsRevoked;
}

/**
 * Check if error indicates an expired refresh token.
 *
 * Refresh tokens have a finite TTL configured in Cognito; expiry is the
 * expected outcome when a user returns after the TTL has elapsed and is not
 * a bug. Callers should clear local session state but should not capture
 * this as a Sentry exception.
 */
export function isExpiredRefreshTokenError(error: ParsedAuthError): boolean {
  const message = error.message.toLowerCase();

  return message.includes('refresh token') && message.includes('expired');
}

/**
 * Check if the error means the Cognito account behind the session is gone or
 * turned off — deleted, disabled, or otherwise no longer able to authenticate.
 *
 * Distinct from a revoked or expired token: the token is not the problem and
 * no refresh will ever succeed, so the only correct response is to clear the
 * local session. Left in place, the stale session keeps issuing requests that
 * 401 forever and the app never returns the person to the login screen.
 */
export function isUnrecoverableAccountError(error: ParsedAuthError): boolean {
  const message = error.message.toLowerCase();

  // Matched as one phrase, not as two loose substrings. This predicate drives
  // a forced sign-out, so any refresh error that merely happened to mention a
  // user and something deleted would otherwise end a perfectly good session.
  const mentionsDeletedUser = message.includes('user has been deleted');

  const mentionsDisabledUser = message.includes('user is disabled');

  const mentionsMissingUser = message.includes('user does not exist');

  return mentionsDeletedUser || mentionsDisabledUser || mentionsMissingUser;
}
